import {
  AssociationEntity,
  IOpportunityQuote,
  QuoteTypeEnumDisplay,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { formatCurrency } from '@monetize/utils/core';
import {
  AppColumnDef,
  createColumnHelper,
} from '../Monetize/DataTableNew/appTable';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { handleApiErrorToast } from '../../api/axios';
import { useGetOpportunityById } from '../../api/cpqService';
import {
  useDealRoomConfig,
  useGetCrmConfiguration,
} from '../../api/settingsService';
import { ROUTES } from '../../constants';
import { OPPORTUNITY_STATUS_DISPLAY } from '../../constants/opportunities';
import {
  QUOTE_SENT_STATUSES,
  QuoteStatusEnumDisplay,
  QuoteStatusTagStyle,
} from '../../constants/quotes';
import { getDealRoomByDealRoom } from '../../constants/routes';
import { useCreateDealRoom } from '../../dealroom/api/dealRoomService';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useACL } from '../../services/acl/acl';
import { useFlags } from '../../services/launchDarkly';
import { getQuoteLink, openQuote } from '../../utils/quotes';
import { ExternalAssociationsIcons } from '../ExternalAssociations/ExternalAssociationsIcons';
import {
  MBox,
  MButton,
  MCenterModal,
  MDivider,
  MFlex,
  MHStack,
  MPageHeader,
  MSkeleton,
  MTag,
  MText,
  MVStack,
} from '../Monetize';
import { DataTable } from '../Monetize/DataTableNew/DataTable';
import {
  ACTION_COLUMN_KEY,
  createActionColumn,
} from '../Monetize/DataTableNew/dataTableNewRenderers';
import {
  currencyBodyTemplate,
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderWithSize,
  nameWithIdBodyTemplate,
} from '../Monetize/DataTableNew/tableUtilsNew';
import { TitleValuePair } from '../Monetize/TitleValuePair';
import { OpportunityLinkCrmOpportunityInput } from './OpportunityLinkCrmOpportunityInput';
import { OpportunityModalActions } from './OpportunityModalActions';
import { OpportunityQuoteActions } from './OpportunityQuoteActions';
import { useCrmOpportunityLink } from './useCrmOpportunityLink';

interface OpportunityModalProps {
  isOpen: boolean;
  opportunityId?: Maybe<string>;
  isDisabled?: boolean;
  onClose: (skipNavigate?: boolean) => void;
}

const columnHelper = createColumnHelper<IOpportunityQuote>();

export const OpportunityModal = ({
  isOpen,
  opportunityId: opportunityIdProp,
  isDisabled,
  onClose,
}: OpportunityModalProps) => {
  const navigate = useNavigate();
  const params = useParams();

  const { canDo } = useACL();
  const canUpdateSales = canDo([['sales', 'update']]);

  const opportunityId = opportunityIdProp || params?.opportunityId || '';
  const { defaultCurrency } = useCurrencies();
  const { data: opportunity, isLoading: isOpportunityLoading } =
    useGetOpportunityById(opportunityId, {
      enabled: isOpen && !!opportunityId,
    });
  const { data: crmConnections } = useGetCrmConfiguration({
    enabled: isOpen && !!opportunityId,
  });
  const crmOpportunityLink = useCrmOpportunityLink({
    opportunityId,
    customId: opportunity?.customId,
    enabled: isOpen,
  });
  const { primaryQuoteId } = opportunity || {};
  const hasProcessedOrSentQuote = !!opportunity?.quotes?.some((quote) =>
    QUOTE_SENT_STATUSES.has(quote.status),
  );
  const { useDealRoom } = useFlags();
  const { data: dealRoomConfig } = useDealRoomConfig();
  const [isCreatingDealRoom, setIsCreatingDealRoom] = useState(false);
  const { mutateAsync: doCreateDealRoom } = useCreateDealRoom({
    onSuccess: () => {
      setIsCreatingDealRoom(false);
    },
    onError: (err) => {
      setIsCreatingDealRoom(false);
      handleApiErrorToast(err);
    },
  });

  // Linking is only supported for Salesforce; other CRMs link via sync.
  const canUserModifyCrmLink =
    !!crmConnections?.salesforce &&
    !isDisabled &&
    canUpdateSales &&
    !hasProcessedOrSentQuote;
  const canCreateDealRoom =
    useDealRoom && !!opportunity?.id && dealRoomConfig?.enabled;

  const actionsTemplate = useCallback(
    (opportunityQuote: IOpportunityQuote) => {
      if (opportunity) {
        return (
          <OpportunityQuoteActions
            isDisabled={isDisabled}
            opportunity={opportunity}
            opportunityQuote={opportunityQuote}
          />
        );
      }
      return null;
    },
    [isDisabled, opportunity],
  );

  const quoteColumns: AppColumnDef<IOpportunityQuote, any>[] = useMemo(
    () => [
      columnHelper.accessor('id', {
        ...getColHeaderWithSize('Name', 250),
        cell: nameWithIdBodyTemplate<IOpportunityQuote, string>({
          nameProp: 'name',
          idProp: 'id',
          idLinkFn: (id, data) =>
            getQuoteLink({ quoteId: id, status: data.status }),
          openInNewTab: true,
        }),
      }),
      columnHelper.display({
        id: 'primary',
        size: 90,
        enableResizing: false,
        cell: ({ row }) => {
          const quote = row.original;
          return (
            quote.id === primaryQuoteId && <MTag variant="blue">Primary</MTag>
          );
        },
        enableSorting: false,
      }),
      columnHelper.accessor('type', {
        ...getColHeaderWithSize('Type', 150),
        cell: enumDisplayBodyTemplate({
          displayMap: QuoteTypeEnumDisplay,
        }),
        enableSorting: false,
      }),
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 130),
        cell: enumDisplayBodyTemplate({
          displayMap: QuoteStatusEnumDisplay,
          styleMap: QuoteStatusTagStyle,
        }),
        enableSorting: false,
      }),
      columnHelper.accessor('total', {
        ...getColHeaderWithSize('Total', 130),
        cell: currencyBodyTemplate({
          currencyProperty: 'currency' as any,
        }),
        enableSorting: false,
      }),
      columnHelper.accessor('modifyDate', {
        ...getColHeaderWithSize('Updated', 170),
        cell: dateTimeBodyTemplate(),
        enableSorting: false,
      }),
      createActionColumn<IOpportunityQuote>(actionsTemplate),
    ],
    [actionsTemplate, primaryQuoteId],
  );

  const title = opportunity && (
    <MVStack alignItems="flex-start">
      <MPageHeader
        title="Opportunity Details"
        copyUrl
        id={opportunity?.id}
        status={OPPORTUNITY_STATUS_DISPLAY[opportunity?.status]}
        containerProps={{ mb: 0 }}
        recordAuditInfo={opportunity}
        extraBadges={[
          <ExternalAssociationsIcons
            key="ExternalAssociationsIcons"
            entity={AssociationEntity.OPPORTUNITY}
            recordId={opportunity.id}
          />,
        ]}
      ></MPageHeader>
    </MVStack>
  );

  const handleCreateDealRoom = async () => {
    if (opportunity?.dealRoomId) {
      navigate(getDealRoomByDealRoom(opportunity.dealRoomId));
    } else {
      setIsCreatingDealRoom(true);
      const updatedQuote = await doCreateDealRoom({
        opportunityId: opportunity?.id!,
      });
      updatedQuote?.opportunity?.dealRoomId &&
        navigate(getDealRoomByDealRoom(updatedQuote?.opportunity?.dealRoomId));
    }
  };

  return (
    <MCenterModal
      isOpen={isOpen}
      onClose={onClose}
      size="6xl"
      modalContentProps={{ 'data-testid': 'opportunity-form' } as any}
      renderModalTitleActions={() => title}
    >
      {isOpportunityLoading && <MSkeleton width={'full'} height={10} mt={4} />}
      {!isOpportunityLoading && opportunity && (
        <MFlex
          justifyContent={'space-between'}
          alignItems={'center'}
          flexWrap={'nowrap'}
        >
          <MHStack gap={6}>
            <MBox maxW={'330'}>
              <TitleValuePair
                title="Opportunity Name"
                value={opportunity?.name}
              />
            </MBox>
            <MBox minW={'145'}>
              <TitleValuePair
                title="Opportunity Amount"
                value={formatCurrency(opportunity?.amount, {
                  currency: opportunity.currency || defaultCurrency,
                })}
              />
            </MBox>

            {/*
                For Salesforce, allow searching for and linking Opportunity
                If user is not allowed to change, still show the link if it exists
               */}
            {opportunity && (canUserModifyCrmLink || !!crmOpportunityLink) && (
              <MBox>
                <TitleValuePair
                  title="CRM Opportunity"
                  value={
                    <OpportunityLinkCrmOpportunityInput
                      isDisabled={!canUserModifyCrmLink}
                      crmLink={crmOpportunityLink}
                      opportunity={opportunity}
                      quotes={opportunity?.quotes || []}
                    />
                  }
                />
              </MBox>
            )}
          </MHStack>

          {opportunity && (
            <MFlex justifyContent={'flex-end'} minW={10} alignItems="center">
              {(!!opportunity?.dealRoomId || canCreateDealRoom) && (
                <MButton
                  variant="secondary"
                  onClick={handleCreateDealRoom}
                  isLoading={isCreatingDealRoom}
                >
                  {opportunity?.dealRoomId
                    ? 'Open Deal Room'
                    : 'Create Deal Room'}
                </MButton>
              )}
              <OpportunityModalActions
                isDisabled={isDisabled}
                opportunity={opportunity}
              />
            </MFlex>
          )}
        </MFlex>
      )}

      <MDivider mt={6} mb={5} borderWidth="2px" borderColor="tBlue.hover" />
      <MText fontSize="md" fontWeight="bold">
        Quotes
      </MText>
      {isOpportunityLoading && <MSkeleton height={100} width="full" mt={8} />}
      {!isOpportunityLoading && (
        <DataTable
          columns={quoteColumns}
          data={opportunity?.quotes || []}
          isLoading={isOpportunityLoading}
          enablePagination={false}
          clientControlled
          pinnedRightColumns={[ACTION_COLUMN_KEY]}
          totalElements={opportunity?.quotes?.length || 0}
          onRowClick={(row) => {
            const quoteId = row.id;
            if (
              [
                ROUTES.getQuoteEditRoute(quoteId),
                ROUTES.getQuoteReviewRoute(quoteId),
              ].includes(window.location.pathname)
            ) {
              onClose(true);
              return;
            }

            openQuote(navigate, quoteId, row.status);
            onClose(true);
          }}
          emptyProps={{
            smallMessage: 'No quotes are associated with this opportunity.',
          }}
        />
      )}
    </MCenterModal>
  );
};
